// Settings Page
import { useQuery } from '@tanstack/react-query';
import { User, Database, Rss, FolderOpen, Tag as TagIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Card';
import { Skeleton } from '@/components/Skeleton';
import { useTheme } from '@/lib/theme';
import { Switch } from '@/components/Switch';
import Button from '@/components/Button';

export default function Settings() {
  const { theme, toggleTheme } = useTheme();

  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: () => api.getUser(),
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => api.getUserStats(),
  });

  const user = userData?.user;
  const stats = statsData?.stats;

  return (
    <div className="h-full overflow-y-auto scrollbar-thin">
      <div className="mx-auto max-w-4xl p-8">
        <h1 className="mb-8 text-3xl font-bold">Settings</h1>

        {/* User Profile */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Profile</CardTitle>
                <CardDescription>Your account information</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {userLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-6 w-64" />
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{user?.name || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account created</p>
                  <p className="font-medium">
                    {user?.created_at
                      ? new Date(user.created_at * 1000).toLocaleDateString()
                      : 'Unknown'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Customize how FluxFeed looks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  {theme === 'dark' ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} />
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Database className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Statistics</CardTitle>
                <CardDescription>Your FluxFeed usage</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <StatCard
                  icon={<Rss className="h-5 w-5" />}
                  label="Feeds"
                  value={stats?.feeds || 0}
                />
                <StatCard
                  icon={<FolderOpen className="h-5 w-5" />}
                  label="Folders"
                  value={stats?.folders || 0}
                />
                <StatCard
                  icon={<TagIcon className="h-5 w-5" />}
                  label="Tags"
                  value={stats?.tags || 0}
                />
                <StatCard
                  icon={<Database className="h-5 w-5" />}
                  label="Articles"
                  value={stats?.articles?.total || 0}
                />
              </div>
            )}

            {stats?.articles && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-2xl font-bold">{stats.articles.unread}</p>
                  <p className="text-sm text-muted-foreground">Unread articles</p>
                </div>
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-2xl font-bold">{stats.articles.starred}</p>
                  <p className="text-sm text-muted-foreground">Starred articles</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* About */}
        <Card>
          <CardHeader>
            <CardTitle>About FluxFeed</CardTitle>
            <CardDescription>Modern RSS reader built on Cloudflare</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <p>
                FluxFeed is a cloud-native, multi-user RSS reader built entirely on the Cloudflare
                stack, featuring AI-powered summaries and full-content rendering.
              </p>
              <div className="space-y-1 text-muted-foreground">
                <p><strong>Stack:</strong> Cloudflare Workers, D1, KV, Workers AI, Browser Rendering</p>
                <p><strong>Frontend:</strong> React, TypeScript, Tailwind CSS</p>
                <p><strong>Version:</strong> 0.1.0 (MVP)</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  View Documentation
                </Button>
                <Button variant="outline" size="sm">
                  Report Issue
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">{icon}</div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
